import moment from 'moment-timezone';
import html2canvas from 'html2canvas';
import { groupByLabel, AgeGroupTypes } from './agegroups';
import {
  imgThumbPostfix,
  storageTournament,
  storageDate,
  storageField,
  storageScrollY,
} from './consts';
import { Genders, CutOffDates } from './enums';

// Clears the persisted "current tournament" selection and its schedule view
// state (date/field filters, scroll position). storageScrollY and
// storageScheduleScrollY share the same key, so these four cover both.
export const clearSavedTournament = () => {
  localStorage.removeItem(storageTournament);
  localStorage.removeItem(storageDate);
  localStorage.removeItem(storageField);
  localStorage.removeItem(storageScrollY);
};

// Date range formater
export function formatDateRange(startDate, endDate) {
  if (!startDate || !endDate) return '';
  if (startDate.isSame(endDate, 'year')) {
    if (startDate.isSame(endDate, 'month')) {
      if (startDate.isSame(endDate, 'day')) {
        return startDate.format('MMM D, YY');
      } else {
        return `${startDate.format('MMM D')}-${endDate.format('D, YY')}`;
      }
    } else {
      return `${startDate.format('MMM D')} – ${endDate.format('MMM D, YY')}`;
    }
  } else {
    return `${startDate.format('MMM D, YY')} – ${endDate.format('MMM D, YY')}`;
  }
}

// Formats a standalone day + time range (e.g. "Sat, Jul 25, 8:00 - 8:30am")
// from two moments. Shares the am/pm dedup with timeStr for consistency.
export function formatDateTimeRange(start, end) {
  if (!start || !end) return '';
  const range = `${start.format('h:mm')} - ${end.format('h:mma')}`
    .toLowerCase()
    .replace(/((?:am|pm))(?=.*\1)/, '');
  return `${start.format('ddd, MMM D')}, ${range}`;
}

// Chat timestamps. Firestore hands back a Timestamp, an optimistic message
// carries a plain Date, and either may be missing while a write is pending.
const toMoment = (value) => {
  if (!value) return null;
  const date = typeof value.toDate === 'function' ? value.toDate() : value;
  const m = moment(date);
  return m.isValid() ? m : null;
};

// Separator between two days of a conversation
export function chatDayLabel(value) {
  const m = toMoment(value);
  if (!m) return '';
  if (m.isSame(moment(), 'day')) return 'Today';
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return m.isSame(moment(), 'year')
    ? m.format('MMM D')
    : m.format('MMM D, YYYY');
}

// The time under a single message
export function chatTimeShort(value) {
  const m = toMoment(value);
  return m ? m.format('h:mma') : '';
}

// The chat list shows a clock for today and a date for anything older
export function chatListTime(value) {
  const m = toMoment(value);
  if (!m) return '';
  return m.isSame(moment(), 'day') ? chatTimeShort(m) : chatDayLabel(m);
}

// Genders formater
export function formatGenders(genders) {
  if (!Array.isArray(genders)) return '';
  return genders.map((g) => switchGender(g)).join(', ');
}

// Ages formater
export function formatAges(ages) {
  if (!Array.isArray(ages)) return '';
  return ages.join(', ');
}

// Convert { date, time, timezone } obj to moment obj
export function getMatchMomentUTC({ date, time, timezone }) {
  return moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', timezone);
}

// Convert moment obj to { date, time, timezone } obj
export function getMatchDateTimeTz(momentObj) {
  return {
    date: momentObj.format('YYYY-MM-DD'),
    time: momentObj.format('HH:mm'),
    timezone: momentObj.tz(),
  };
}

// Convert time from 24 to 12-hour format
export function time24To12H(time) {
  return moment(time, 'HH:mm').format('h:mma');
}

// Convert time from 12 to 24-hour format
export function time12To24H(time) {
  const cleaned = time.replace(/\s+/g, '');
  return moment(cleaned, 'h:mma').format('HH:mm');
}

// Convert hours to time in 12-hour format
export function hourTo12H(hour) {
  return moment({ hour }).format('h:mm a');
}

// Converts a 12-hour clock time string with AM/PM into the total number of minutes past midnight
export const parseTimeToMinutes = (timeStr) => {
  const [time, period] = timeStr
    .toLowerCase()
    .replace(/\s+/g, '')
    .match(/(\d+:\d+)(am|pm)/)
    .slice(1);
  let [hours, minutes] = time.split(':').map(Number);
  if (period === 'pm' && hours !== 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

// Add thumbnail postfix to filename
export function addThumbnailPostfix(filename) {
  if (!filename) return '';

  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1) return filename + imgThumbPostfix;

  const name = filename.slice(0, dotIndex);
  const extension = filename.slice(dotIndex);

  return `${name}${imgThumbPostfix}${extension}`;
}

// Finds the key corresponding to a given value in an object
export const getKeyByValue = (obj, value) => {
  const entry = Object.entries(obj).find(([_, v]) => v === value);
  return entry ? entry[0] : undefined;
};

// Switch 'Male', 'Female' to 'Boys', 'Girls' and back
export const switchGender = (gender) => {
  switch (gender) {
    case Genders.MALE:
      return 'Boys';
    case Genders.FEMALE:
      return 'Girls';
    case 'Boys':
      return Genders.MALE;
    case 'Girls':
      return Genders.FEMALE;
    default:
      return gender;
  }
};

// Returns a formatted time range string (e.g. "9:00–10:30am") from start time and duration
export const timeStr = (start, duration) => {
  const endTime = moment(start, 'HH:mm')
    .add(duration, 'minutes')
    .format('h:mma');

  return `${time24To12H(start)} - ${endTime}`
    .toLowerCase()
    .replace(/((?:am|pm))(?=.*\1)/, '');
};

// Return team's division as a string
export function getTeamDivision(team) {
  return team ? `${team.sport}, ${switchGender(team.gender)}, ${team.age}` : '';
}

// Capitalizing the first letter of a string
export function capFirstLetter(string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

// Decapitalizing the first letter of a string
export function decapFirstLetter(string) {
  if (!string) return '';
  return string.charAt(0).toLowerCase() + string.slice(1);
}

// Strips emailConsent and smsConsent from a payload object
export const omitConsent = ({ emailConsent, smsConsent, ...rest }) => rest;

// Wraps a profile update fn: strips the consent fields unless editing one's own
// profile (backend rejects foreign consent edits).
export function wrapProfileUpdate(updateFn, isOwnProfile) {
  return (payload) => updateFn(isOwnProfile ? payload : omitConsent(payload));
}

// HOF that converts Date-like fields to 'YYYY-MM-DD' strings and shapes card object
export const wrapNormalizeDates =
  (fn) =>
  (values = {}) => {
    const { birthDate, card, ...rest } = values || {};

    const fmt = (d) => {
      if (!d) return undefined;
      return moment(d).format('YYYY-MM-DD');
    };

    const out = { ...rest };
    const bd = fmt(birthDate);
    if (bd) out.birthDate = bd;
    const { expireDate, issueDate, number } = card || {};
    if (expireDate || issueDate || number) {
      out.card = {};
      const edate = fmt(expireDate);
      const idate = fmt(issueDate);
      if (edate) out.card.expireDate = edate;
      if (idate) out.card.issueDate = idate;
      if (number) out.card.number = number;
    }

    return fn(out);
  };

// Creates a debounced function that delays invoking func until after wait ms.
// `.cancel()` drops a scheduled call — for a caller whose input stopped
// mattering (a picked suggestion, a cleared field, an unmounted component)
export function debounce(func, wait = 200) {
  let timeoutId = null;

  const debounced = function (...args) {
    // If called again before wait, clear the previous timer
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    // Schedule func to run after wait milliseconds
    timeoutId = setTimeout(() => {
      timeoutId = null;
      func.apply(this, args);
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId !== null) clearTimeout(timeoutId);
    timeoutId = null;
  };

  return debounced;
}

// Checks if a player is too old for an age group based on birth date and age cutoff date (Aug 1 or Jan 1)
export function isTooOld({ birthDate, age, cutOffDate }) {
  const cutOffDateConfig = {
    [CutOffDates.AUG_1]: { month: 7, day: 1 },
    [CutOffDates.JAN_1]: { month: 0, day: 1 },
  };

  const selectedCutOff = cutOffDateConfig[cutOffDate];
  if (!birthDate || !age || !selectedCutOff) return false;

  const now = moment();
  const cutOffMoment = moment()
    .year(now.year())
    .month(selectedCutOff.month)
    .date(selectedCutOff.day)
    .startOf('day');

  if (now.isBefore(cutOffMoment)) cutOffMoment.subtract(1, 'year');

  const group = groupByLabel(age);

  if (!group || group.type !== AgeGroupTypes.U) return false;

  const birthMoment = moment(birthDate).startOf('day');
  const threshold = cutOffMoment.clone().subtract(group.value, 'years');

  return birthMoment.isBefore(threshold, 'day');
}

// Returns number of rounds, start round, and games in each round for a given team count in a division
export function calcBracketDepth(teamCount) {
  if (teamCount <= 1)
    return {
      numRounds: 0,
      startRound: null,
      gamesEachRound: [],
      prelimGames: 0,
    };

  const largestPow2 = 2 ** Math.floor(Math.log2(teamCount));
  const prelimGames = teamCount - largestPow2;

  const gamesEachRound = [];
  if (prelimGames > 0) gamesEachRound.push(prelimGames);
  let bracketSize = largestPow2;
  while (bracketSize > 1) {
    bracketSize /= 2;
    gamesEachRound.push(bracketSize);
  }

  const numRounds = gamesEachRound.length;
  const startRound =
    ['F', 'SF', 'QF', 'R16', 'R32', 'R64', 'R128'][numRounds - 1] || null;
  return { numRounds, startRound, gamesEachRound, prelimGames };
}

// Maps array of objects to MultiSelect/MultiSelectInline `options` shape.
// Picks `value` and `label` fields by key (default: 'id' and 'name'),
// sorts by label case-insensitively unless `sort: false`.
export function toOptions(
  items,
  { value = 'id', label = 'name', sort = true } = {},
) {
  const opts = (items || []).map((it) => ({
    value: it[value],
    label: it[label],
  }));
  return sort
    ? opts.sort((a, b) =>
        String(a.label).localeCompare(String(b.label), 'en', {
          sensitivity: 'base',
        }),
      )
    : opts;
}

// Captures a DOM element as a JPEG Blob via html2canvas
export const captureJpegBlob = async (element) => {
  await new Promise((r) => setTimeout(r, 0));
  const canvas = await html2canvas(element, {
    useCORS: true,
    backgroundColor: '#ffffff',
    scrollX: 0,
    scrollY: 0,
  });
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
  });
};

// The tail of a picked value is «<ST>» or «<ST> <postcode>». The postcode is
// only required to carry a digit, so a non-US one buckets too; without that
// lookahead «St Louis» would read as the state ST.
const LOCATION_TAIL =
  /^([A-Za-z]{2})(?:\s+(?=\S*\d)[A-Za-z0-9][A-Za-z0-9 -]*)?$/;

// The location picker always ends a value with ", <City>, <ST>[ <postcode>]",
// so the filter key reads back without parsing the rest. A hand-typed legacy
// value matches nothing and is returned whole — the bucket it already occupies.
// Always a string: the lists render the result as a filter option.
export function cityOf(location) {
  const str = String(location ?? '').trim();
  const parts = str
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return str;
  const state = parts[parts.length - 1].match(LOCATION_TAIL);
  const city = parts[parts.length - 2];
  if (!state || !city) return str;
  return `${city}, ${state[1].toUpperCase()}`;
}

// PDF/report title: «Name, Location, Jan 5 – 7, 2026». Every part but the
// address is glued with non-breaking spaces, so only the address may wrap.
export function reportTitle({ name, location, startDate, endDate }) {
  const noWrap = (str) => String(str ?? '').replace(/\s/g, '\u00A0');
  return [noWrap(name), location, noWrap(formatDateRange(startDate, endDate))]
    .filter(Boolean)
    .join(', ');
}
